import { forwardRef, memo } from "react";
import { cn } from "@/lib/utils";
import { NavRow } from "@/components/ui/nav-row";
import { getPresenceState, presenceDotClass } from "@/lib/presence";
import {
  EnvelopeSimple,
  Handshake,
  LinkSimple,
  UserCircle,
} from "@/components/ui/icons";
import { CoworkerIcon } from "@/components/ui/coworker-icon";
import { csCountRender } from "@/lib/channel-switch-profile";
import type { DmRow } from "./dm-display-filters";
import type { DmActions } from "./use-dm-actions";

interface DMRowItemProps {
  dm: DmRow;
  isSelected: boolean;
  isUnread: boolean;
  isCollaborator?: boolean;
  actions: DmActions;
  inviteBadge?: string | null;
  inviteMethod?: "email" | "link" | null;
}

/**
 * Single DM row body (avatar, name, badges). Hover actions and the
 * shared-workspace tooltip live in `DmRow` so `group/dm` hover is
 * never split by a tooltip wrapper.
 *
 * Wrapped in `memo()` + consumes `actions: DmActions` (stable ref) so
 * identity-stable props let `DMRowItem.memo` bail out cleanly on
 * Sidebar re-renders.
 */
export const DMRowItem = memo(
  forwardRef<HTMLButtonElement, DMRowItemProps>(function DMRowItem(
    {
      dm,
      isSelected,
      isUnread,
      isCollaborator,
      actions,
      inviteBadge,
      inviteMethod,
    },
    ref,
  ) {
    csCountRender("DMRowItem");

    return (
      <NavRow
        ref={ref}
        data-sidebar-channel-id={dm.channelId}
        isSelected={isSelected}
        onClick={() => actions.selectChannel(dm.channelId)}
        className="pr-8"
      >
        <div className="relative">
          <div className="h-5 w-5 rounded-full bg-foreground/20 flex items-center justify-center text-[9px] font-semibold overflow-hidden">
            {dm.avatarUrl ? (
              <img
                src={dm.avatarUrl}
                alt={dm.displayName}
                className="h-full w-full object-cover"
              />
            ) : dm.isGuest ? (
              <UserCircle size={14} className="text-muted-foreground" />
            ) : (
              dm.displayName.slice(0, 2).toUpperCase()
            )}
          </div>
          <div
            className={cn(
              "absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full",
              presenceDotClass(
                getPresenceState({
                  is_online: dm.isOnline,
                  last_active_at: dm.lastActiveAt,
                }),
              ),
            )}
          />
        </div>
        <span
          className={cn(
            "min-w-0 flex-1 truncate",
            isUnread && "font-bold text-foreground",
          )}
        >
          {dm.displayName}
        </span>
        {dm.isCoworker && <CoworkerIcon size={12} className="shrink-0" />}
        {isCollaborator && <Handshake size={12} className="shrink-0" />}
        {inviteMethod && (
          <span className="shrink-0 flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded-full bg-muted text-muted-foreground max-w-[100px] truncate">
            {inviteMethod === "email" ? (
              <>
                <EnvelopeSimple size={9} className="shrink-0" />
                <span className="truncate">{inviteBadge}</span>
              </>
            ) : (
              <>
                <LinkSimple size={9} className="shrink-0" />
                via link
              </>
            )}
          </span>
        )}
        {isUnread && (
          <span className="ml-auto h-2 w-2 rounded-full bg-foreground shrink-0 transition-opacity group-hover/dm:opacity-25 group-focus-within/dm:opacity-25" />
        )}
      </NavRow>
    );
  }),
);
DMRowItem.displayName = "DMRowItem";
